import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { hesaplaBakimDurumu, type BakimEsikler, type BakimDurum, telefonNormalize } from '@/pages/bakim-takip/utils/bakimHesapla';

export interface Musteri {
  id: string;
  ad: string;
  soyad: string;
  telefon: string;
  alternatif_telefon: string | null;
  acik_adres: string | null;
  il: string | null;
  ilce: string | null;
  adres_tarifi: string | null;
  google_maps_url: string | null;
  kayit_tarihi: string | null;
  musteri_durumu: string | null;
  genel_notlar: string | null;
  aktif: boolean;
  cihaz_sayisi: number;
  son_servis_tarihi: string | null;
  son_bakim_tarihi: string | null;
  bir_sonraki_bakim_tarihi: string | null;
  toplam_harcama: number;
  bakim_durumu: BakimDurum | 'yok';
}

export interface MusteriFormData {
  ad: string;
  soyad: string;
  telefon: string;
  alternatif_telefon: string;
  acik_adres: string;
  il: string;
  ilce: string;
  adres_tarifi: string;
  google_maps_url: string;
  musteri_durumu: string;
  genel_notlar: string;
}

export interface IlkServisFormData {
  islem_kaydi_olustur: boolean;
  islem_turu: string;
  diger_aciklama: string;
  servis_tarihi: string;
  servis_saati: string;
  tahmini_ucret: string;
  odeme_durumu: string;
  odeme_tipi: string;
  personel_id: string;
  islem_aciklamasi: string;
  servis_notu: string;
}

export const ILK_SERVIS_BOS: IlkServisFormData = {
  islem_kaydi_olustur: true,
  islem_turu: 'Filtre Bakımı – Dörtlü',
  diger_aciklama: '',
  servis_tarihi: new Date().toISOString().split('T')[0],
  servis_saati: '09:00',
  tahmini_ucret: '',
  odeme_durumu: 'odendi',
  odeme_tipi: 'Nakit',
  personel_id: '',
  islem_aciklamasi: '',
  servis_notu: '',
};

export const ISLEM_TURLERI = [
  'Filtre Bakımı – Dörtlü',
  'Filtre Bakımı – Beşli',
  'Genel Bakım',
  'Yeni Montaj',
  'Bina Girişi',
  'Bina Girişi Bakım',
  'Taşıma',
  'Diğer',
] as const;

export const ODEME_DURUMLARI = [
  { value: 'odendi', label: 'Ödendi' },
  { value: 'bekliyor', label: 'Ödeme Bekliyor' },
  { value: 'kismi', label: 'Kısmi Ödendi' },
];

export const ODEME_TIPLERI = ['Nakit', 'Kart', 'IBAN', 'Diğer'] as const;

export type FilterType = 'hepsi' | 'yesil' | 'sari' | 'turuncu' | 'kirmizi';

const PAGE_SIZE = 15;

export function useCustomers() {
  const [customers, setCustomers] = useState<Musteri[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('hepsi');
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // useRef kullanarak esikler değiştiğinde fetchCustomers'ı yeniden tetiklemiyoruz
  const esiklerRef = useRef<BakimEsikler>({ sariAy: 6, turuncuAy: 9, kirmiziAy: 12 });

  const fetchAyarlar = useCallback(async () => {
    const { data } = await supabase
      .from('sadem_firma_ayarlari')
      .select('sari_baslangic_ay, turuncu_baslangic_ay, kirmizi_baslangic_ay')
      .maybeSingle();
    if (data) {
      const yeniEsikler = {
        sariAy: data.sari_baslangic_ay ?? 6,
        turuncuAy: data.turuncu_baslangic_ay ?? 9,
        kirmiziAy: data.kirmizi_baslangic_ay ?? 12,
      };
      // Sadece gerçekten değiştiyse güncelle (obje referans karşılaştırmasını engellemek için)
      if (
        esiklerRef.current.sariAy !== yeniEsikler.sariAy ||
        esiklerRef.current.turuncuAy !== yeniEsikler.turuncuAy ||
        esiklerRef.current.kirmiziAy !== yeniEsikler.kirmiziAy
      ) {
        esiklerRef.current = yeniEsikler;
      }
    }
  }, []);

  // fetchCustomers artık sadece search'e bağlı, esikler'e değil (esikler ref üzerinden okunuyor)
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchAyarlar();

      let query = supabase
        .from('sadem_musteriler')
        .select('*', { count: 'exact' })
        .eq('silindi', false)
        .order('created_at', { ascending: false });

      if (search.trim()) {
        const s = search.trim();
        query = query.or(`ad.ilike.%${s}%,soyad.ilike.%${s}%,telefon.ilike.%${s}%,acik_adres.ilike.%${s}%,ilce.ilike.%${s}%`);
      }

      const { data: rawCustomers, count, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      let musteriler = (rawCustomers || []) as unknown as Musteri[];
      const total = count ?? 0;
      setTotalCount(total);

      if (musteriler.length === 0) {
        setCustomers([]);
        setLoading(false);
        return;
      }

      const musteriIds = musteriler.map(m => m.id);

      const [
        { data: cihazData },
        { data: servisData },
        { data: bakimData },
      ] = await Promise.all([
        supabase.from('sadem_cihazlar').select('musteri_id').in('musteri_id', musteriIds).eq('aktif', true),
        supabase.from('sadem_servis_kayitlari').select('musteri_id, servis_tarihi, alinan_ucret, odeme_durumu').in('musteri_id', musteriIds).eq('aktif', true),
        supabase.from('sadem_bakim_takibi').select('musteri_id, son_bakim_tarihi, bir_sonraki_bakim_tarihi').in('musteri_id', musteriIds).eq('aktif', true).order('son_bakim_tarihi', { ascending: false }),
      ]);

      const cihazCountMap: Record<string, number> = {};
      (cihazData || []).forEach((c: { musteri_id: string }) => {
        cihazCountMap[c.musteri_id] = (cihazCountMap[c.musteri_id] || 0) + 1;
      });

      const servisMap: Record<string, { sonTarih: string | null; toplam: number }> = {};
      (servisData || []).forEach((s: { musteri_id: string; servis_tarihi: string | null; alinan_ucret: number | null; odeme_durumu: string | null }) => {
        if (!servisMap[s.musteri_id]) {
          servisMap[s.musteri_id] = { sonTarih: null, toplam: 0 };
        }
        if (s.servis_tarihi && (!servisMap[s.musteri_id].sonTarih || s.servis_tarihi > servisMap[s.musteri_id].sonTarih!)) {
          servisMap[s.musteri_id].sonTarih = s.servis_tarihi;
        }
        if (s.odeme_durumu === 'Ödendi' || s.odeme_durumu === 'odendi' || s.odeme_durumu === 'Kısmi Ödendi' || s.odeme_durumu === 'kismi') {
          servisMap[s.musteri_id].toplam += Number(s.alinan_ucret) || 0;
        }
      });

      const bakimMap: Record<string, { sonBakim: string | null; sonrakiBakim: string | null }> = {};
      (bakimData || []).forEach((b: { musteri_id: string; son_bakim_tarihi: string | null; bir_sonraki_bakim_tarihi: string | null }) => {
        if (!bakimMap[b.musteri_id]) {
          bakimMap[b.musteri_id] = { sonBakim: b.son_bakim_tarihi, sonrakiBakim: b.bir_sonraki_bakim_tarihi };
        }
      });

      const enriched: Musteri[] = musteriler.map(m => ({
        ...m,
        cihaz_sayisi: cihazCountMap[m.id] || 0,
        son_servis_tarihi: servisMap[m.id]?.sonTarih ?? null,
        toplam_harcama: servisMap[m.id]?.toplam ?? 0,
        son_bakim_tarihi: bakimMap[m.id]?.sonBakim ?? null,
        bir_sonraki_bakim_tarihi: bakimMap[m.id]?.sonrakiBakim ?? null,
        bakim_durumu: hesaplaBakimDurumu(bakimMap[m.id]?.sonBakim ?? null, esiklerRef.current),
      }));

      setCustomers(enriched);
    } catch (err) {
      setError('Müşteri bilgileri yüklenirken bir hata oluştu.');
      console.error('[useCustomers] fetch hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [search, fetchAyarlar]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const durumaGoreFiltrelenmis = useCallback(() => {
    if (filter === 'hepsi') return customers;
    return customers.filter(c => c.bakim_durumu === filter);
  }, [customers, filter]);

  const filtered = durumaGoreFiltrelenmis();
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  const durumCounts = {
    hepsi: customers.length,
    yesil: customers.filter(c => c.bakim_durumu === 'yesil').length,
    sari: customers.filter(c => c.bakim_durumu === 'sari').length,
    turuncu: customers.filter(c => c.bakim_durumu === 'turuncu').length,
    kirmizi: customers.filter(c => c.bakim_durumu === 'kirmizi').length,
  };

  const saveCustomer = useCallback(async (data: MusteriFormData, editId?: string | null) => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const normalizedTelefon = telefonNormalize(data.telefon);
      const normalizedAlternatif = data.alternatif_telefon ? telefonNormalize(data.alternatif_telefon) : null;

      if (editId) {
        const { error: updateError } = await supabase
          .from('sadem_musteriler')
          .update({
            ad: data.ad,
            soyad: data.soyad,
            telefon: normalizedTelefon,
            alternatif_telefon: normalizedAlternatif,
            acik_adres: data.acik_adres || null,
            il: data.il || null,
            ilce: data.ilce || null,
            adres_tarifi: data.adres_tarifi || null,
            google_maps_url: data.google_maps_url || null,
            musteri_durumu: data.musteri_durumu || null,
            genel_notlar: data.genel_notlar || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editId);

        if (updateError) {
          console.error('[useCustomers] update hatası:', { tablo: 'sadem_musteriler', code: updateError.code, message: updateError.message, details: updateError.details, hint: updateError.hint });
          throw updateError;
        }
        setSaveSuccess('Müşteri bilgileri güncellendi.');
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from('sadem_musteriler')
          .insert({
            ad: data.ad,
            soyad: data.soyad,
            telefon: normalizedTelefon,
            alternatif_telefon: normalizedAlternatif,
            acik_adres: data.acik_adres || null,
            il: data.il || null,
            ilce: data.ilce || null,
            adres_tarifi: data.adres_tarifi || null,
            google_maps_url: data.google_maps_url || null,
            kayit_tarihi: new Date().toISOString().split('T')[0],
            musteri_durumu: data.musteri_durumu || null,
            genel_notlar: data.genel_notlar || null,
            aktif: true,
            silindi: false,
          })
          .select('id')
          .single();

        if (insertError) {
          console.error('[useCustomers] insert hatası:', { tablo: 'sadem_musteriler', code: insertError.code, message: insertError.message, details: insertError.details, hint: insertError.hint });
          throw insertError;
        }

        setSaveSuccess('Müşteri kaydı başarıyla oluşturuldu.');
        return { ok: true, newId: inserted?.id };
      }
      await fetchCustomers();
      return { ok: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Müşteri kaydı oluşturulamadı. Lütfen zorunlu alanları ve bağlantınızı kontrol ediniz.';
      setSaveError(msg);
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [fetchCustomers]);

  const createIlkServisKaydi = useCallback(async (musteriId: string, servisData: IlkServisFormData) => {
    try {
      const { error: servisError } = await supabase
        .from('sadem_servis_kayitlari')
        .insert({
          musteri_id: musteriId,
          personel_id: servisData.personel_id || null,
          servis_tarihi: servisData.servis_tarihi,
          servis_saati: servisData.servis_saati,
          islem_turu: servisData.islem_turu,
          diger_aciklama: servisData.islem_turu === 'Diğer' ? servisData.diger_aciklama : '',
          servis_durumu: 'planlandi',
          yapilan_islem_aciklamasi: servisData.islem_aciklamasi || '',
          servis_notu: servisData.servis_notu || '',
          alinan_ucret: servisData.tahmini_ucret ? Number(servisData.tahmini_ucret) : 0,
          odeme_durumu: servisData.odeme_durumu || 'bekliyor',
          odeme_tipi: servisData.odeme_tipi || 'Nakit',
          aktif: true,
        });

      if (servisError) {
        console.error('[useCustomers] ilk servis insert hatası:', { tablo: 'sadem_servis_kayitlari', code: servisError.code, message: servisError.message, details: servisError.details, hint: servisError.hint });
        return { ok: false, error: servisError.message };
      }
      return { ok: true };
    } catch (err) {
      console.error('[useCustomers] ilk servis beklenmeyen hata:', err);
      return { ok: false, error: 'Servis kaydı oluşturulamadı.' };
    }
  }, []);

  const deleteCustomer = useCallback(async (id: string) => {
    setSaveError(null);
    try {
      const { error: delError } = await supabase
        .from('sadem_musteriler')
        .update({ silindi: true, aktif: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (delError) throw delError;
      setSaveSuccess('Müşteri başarıyla silindi.');
      await fetchCustomers();
      return true;
    } catch (err) {
      setSaveError('Müşteri silinirken bir hata oluştu.');
      return false;
    }
  }, [fetchCustomers]);

  const clearMessages = useCallback(() => {
    setSaveError(null);
    setSaveSuccess(null);
  }, []);

  return {
    paginated,
    totalPages,
    page,
    setPage,
    totalCount: filtered.length,
    loading,
    error,
    search,
    setSearch,
    filter,
    setFilter,
    durumCounts,
    saving,
    saveError,
    saveSuccess,
    saveCustomer,
    createIlkServisKaydi,
    deleteCustomer,
    clearMessages,
    refetch: fetchCustomers,
  };
}